/**
 * Admin Profile Page
 * 관리자 프로필 페이지
 */

import React, { useState, useEffect } from 'react';
import { useAdminAuth } from '../../use-cases';
import {
  isSubperAdmin,
  isOperator,
  getRoleDisplayName,
  getServiceDisplayName,
} from '../../models';

export const AdminProfilePage: React.FC = () => {
  const { currentUser, loading, error, updateProfile, refreshProfile } =
    useAdminAuth();

  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    if (currentUser) {
      setName(currentUser.name);
      setPhone(currentUser.phone || '');
    }
  }, [currentUser]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSuccessMessage('');

    const success = await updateProfile({ name, phone });

    if (success) {
      setSuccessMessage('프로필이 성공적으로 업데이트되었습니다.');
      setIsEditing(false);
      await refreshProfile();
    }
  };

  const handleCancel = () => {
    if (currentUser) {
      setName(currentUser.name);
      setPhone(currentUser.phone || '');
    }
    setIsEditing(false);
  };

  if (!currentUser) {
    return (
      <div className="p-8 text-center">
        <p className="text-gray-600">로그인이 필요합니다.</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <h1 className="text-3xl font-bold text-gray-900 mb-6">내 프로필</h1>

      {/* Success Message */}
      {successMessage && (
        <div className="mb-6 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
          {successMessage}
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      <div className="bg-white shadow rounded-lg">
        {/* Profile Header */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-indigo-50 to-blue-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="h-16 w-16 bg-indigo-600 rounded-full flex items-center justify-center text-white text-2xl font-bold">
                {currentUser.name.charAt(0)}
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900">
                  {currentUser.name}
                </h2>
                <p className="text-sm text-gray-600">{currentUser.email}</p>
              </div>
            </div>
            <div>
              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  isSubperAdmin(currentUser)
                    ? 'bg-purple-100 text-purple-800'
                    : 'bg-blue-100 text-blue-800'
                }`}
              >
                {getRoleDisplayName(currentUser.role)}
              </span>
            </div>
          </div>
        </div>

        {/* Profile Form */}
        <form onSubmit={handleSubmit} className="px-6 py-6 space-y-6">
          {/* Name Field */}
          <div>
            <label className="block text-sm font-medium text-gray-700">
              이름
            </label>
            {isEditing ? (
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              />
            ) : (
              <p className="mt-1 text-gray-900">{currentUser.name}</p>
            )}
          </div>

          {/* Email Field (Read-only) */}
          <div>
            <label className="block text-sm font-medium text-gray-700">
              이메일
            </label>
            <p className="mt-1 text-gray-900">{currentUser.email}</p>
            <p className="mt-1 text-xs text-gray-500">
              이메일은 변경할 수 없습니다.
            </p>
          </div>

          {/* Phone Field */}
          <div>
            <label className="block text-sm font-medium text-gray-700">
              전화번호
            </label>
            {isEditing ? (
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="010-1234-5678"
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              />
            ) : (
              <p className="mt-1 text-gray-900">
                {currentUser.phone || '등록되지 않음'}
              </p>
            )}
          </div>

          {/* Role Field (Read-only) */}
          <div>
            <label className="block text-sm font-medium text-gray-700">
              역할
            </label>
            <p className="mt-1 text-gray-900">
              {getRoleDisplayName(currentUser.role)}
            </p>
          </div>

          {/* Service Type Field (Operator only, Read-only) */}
          {isOperator(currentUser) && currentUser.service_type && (
            <div>
              <label className="block text-sm font-medium text-gray-700">
                담당 서비스
              </label>
              <p className="mt-1 text-gray-900">
                {getServiceDisplayName(currentUser.service_type)}
              </p>
            </div>
          )}

          {/* Status Fields */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                계정 상태
              </label>
              <p className="mt-1">
                <span
                  className={`px-2 py-1 rounded text-sm ${
                    currentUser.is_active
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}
                >
                  {currentUser.is_active ? '활성' : '비활성'}
                </span>
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                정지 여부
              </label>
              <p className="mt-1">
                <span
                  className={`px-2 py-1 rounded text-sm ${
                    currentUser.is_suspended
                      ? 'bg-red-100 text-red-800'
                      : 'bg-green-100 text-green-800'
                  }`}
                >
                  {currentUser.is_suspended ? '정지됨' : '정상'}
                </span>
              </p>
            </div>
          </div>

          {/* Last Login */}
          {currentUser.last_login && (
            <div>
              <label className="block text-sm font-medium text-gray-700">
                마지막 로그인
              </label>
              <p className="mt-1 text-gray-900">
                {new Date(currentUser.last_login).toLocaleString('ko-KR')}
              </p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex space-x-3">
            {isEditing ? (
              <>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-indigo-600 text-white py-2 px-4 rounded-md hover:bg-indigo-700 disabled:bg-gray-400"
                >
                  {loading ? '저장 중...' : '저장'}
                </button>
                <button
                  type="button"
                  onClick={handleCancel}
                  className="flex-1 bg-gray-200 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-300"
                >
                  취소
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={() => setIsEditing(true)}
                className="flex-1 bg-indigo-600 text-white py-2 px-4 rounded-md hover:bg-indigo-700"
              >
                수정
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};
